import { Anchor, Card, Stack, Table, Text, Title } from '@mantine/core';
import { wtcMarkOf } from '@opti-sea/risq';
import { isRisqStatus } from '../status';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, usePositionNames } from '../../../ui/components';
import { formatDate, formatDateTime } from '../../../ui/format';
import { useAccessHistory, useNames, useRecentEvents, useVesselItems } from '../data';
import type { VesselItem } from '../model';

interface EventRow {
  id: string;
  item_id: string;
  actor_id: string;
  actor_position: string | null;
  type: string;
  from_status: string | null;
  to_status: string | null;
  text: string | null;
  checked: boolean | null;
  not_applicable: boolean | null;
  superseded: boolean;
  client_created_at: string;
}

/**
 * Text of one history entry (status, note, photo, check mark — Done, N/A with the reason, or
 * removed, Q32 — question of the week). `pointText` names the check point when it is known.
 */
export function useEventText() {
  const { t } = useTranslation(['dashboard', 'common']);
  const status = (value: string | null) =>
    isRisqStatus(value) ? t(`common:status.${value}`) : t('activity.unknown');
  return (
    e: Pick<
      EventRow,
      'type' | 'from_status' | 'to_status' | 'text' | 'checked' | 'not_applicable' | 'superseded'
    >,
    pointText?: string | null,
  ) => {
    const base = (() => {
      switch (e.type) {
        case 'status':
          return t('activity.status', { from: status(e.from_status), to: status(e.to_status) });
        case 'note':
          return t('activity.note', { text: e.text ?? '' });
        case 'evidence_added':
          return t('activity.photoAdded');
        case 'evidence_removed':
          return t('activity.photoRemoved');
        case 'wtc_check': {
          const mark = wtcMarkOf(
            e.checked === null ? null : { checked: e.checked, notApplicable: e.not_applicable },
          );
          const line = t(`activity.mark.${mark}`, { point: pointText ?? t('activity.aPoint') });
          return mark === 'not_applicable' && e.text !== null
            ? t('activity.markReason', { line, reason: e.text })
            : line;
        }
        case 'weekly_assigned':
          return t('activity.weekly');
        default:
          return t('activity.unknown');
      }
    })();
    return e.superseded ? t('activity.superseded', { text: base }) : base;
  };
}

export function RecentActivity({
  vesselId,
  items,
  limit,
}: {
  vesselId: string;
  items: readonly VesselItem[];
  limit: number;
}) {
  const { t } = useTranslation('dashboard');
  const events = useRecentEvents(vesselId, limit);
  const names = useNames(vesselId);
  const { name } = usePositionNames();
  const describe = useEventText();
  const byId = new Map(items.map((i) => [i.id, i]));
  return (
    <QueryState loading={events.isPending} error={events.error}>
      {() =>
        (events.data ?? []).length === 0 ? (
          <Text>{t('activity.empty')}</Text>
        ) : (
          <Table.ScrollContainer minWidth={700}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('activity.when')}</Table.Th>
                  <Table.Th>{t('activity.who')}</Table.Th>
                  <Table.Th>{t('activity.question')}</Table.Th>
                  <Table.Th>{t('activity.what')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(events.data ?? []).map((e) => {
                  const item = byId.get(e.item_id);
                  const who = names.data?.get(e.actor_id) ?? t('activity.someone');
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>{formatDateTime(e.client_created_at)}</Table.Td>
                      <Table.Td>
                        {e.actor_position === null
                          ? t('activity.office', { name: who })
                          : t('activity.person', { name: who, position: name(e.actor_position) })}
                      </Table.Td>
                      <Table.Td>
                        {item === undefined ? null : (
                          <Anchor component={Link} to={`/vessels/${vesselId}/questions/${item.id}`}>
                            {item.question.code}
                          </Anchor>
                        )}
                      </Table.Td>
                      <Table.Td>{describe(e)}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )
      }
    </QueryState>
  );
}

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Plain words for an audit entry of the vessel: access changes, inspection planning. */
function useAuditText() {
  const { t } = useTranslation('dashboard');
  const { name } = usePositionNames();
  return (action: string, details: unknown) => {
    const d = (details ?? {}) as Record<string, unknown>;
    const row = (d.new ?? d.old ?? {}) as Record<string, unknown>;
    const position = text(row.position_code);
    const date = (value: unknown) =>
      text(value) === null ? t('activity.noDate') : formatDate(String(value));
    switch (action) {
      case 'vessel_assignments.created':
        return t('activity.assigned', { position: position === null ? '' : name(position) });
      case 'vessel_assignments.revoked':
        return t('activity.revoked', { position: position === null ? '' : name(position) });
      case 'risq_inspections.planned':
        return t('activity.inspectionPlanned', { from: date(d.old), to: date(d.new) });
      case 'risq_inspections.last_voyage':
        return t('activity.lastVoyage', { from: date(d.old), to: date(d.new) });
      default:
        return t('activity.action', { action });
    }
  };
}

function AccessHistory({ vesselId }: { vesselId: string }) {
  const { t } = useTranslation('dashboard');
  const audit = useAccessHistory(vesselId);
  const names = useNames(vesselId);
  const describe = useAuditText();
  return (
    <QueryState loading={audit.isPending} error={audit.error}>
      {() =>
        (audit.data ?? []).length === 0 ? (
          <Text>{t('activity.empty')}</Text>
        ) : (
          <Table verticalSpacing="xs">
            <Table.Tbody>
              {(audit.data ?? []).map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{formatDateTime(row.created_at)}</Table.Td>
                  <Table.Td>
                    {row.actor_id === null
                      ? t('activity.system')
                      : (names.data?.get(row.actor_id) ?? t('activity.someone'))}
                  </Table.Td>
                  <Table.Td>{describe(row.action, row.details)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )
      }
    </QueryState>
  );
}

/** D11 Activity: vessel timeline — status changes, notes, photos; access and planning changes. */
export function ActivityPage() {
  const { t } = useTranslation('dashboard');
  const { vesselId = '' } = useParams();
  const { vessels } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const { items } = useVesselItems(vesselId);
  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  return (
    <>
      <PageTitle
        title={t('activity.title', { vessel: vessel.name })}
        back={{ to: `/vessels/${vessel.id}`, label: vessel.name }}
      />
      <Stack gap="lg">
        <Card withBorder>
          <RecentActivity vesselId={vessel.id} items={items ?? []} limit={200} />
        </Card>
        <Card withBorder>
          <Stack>
            <Title order={2} size="h3">
              {t('activity.accessTitle')}
            </Title>
            <AccessHistory vesselId={vessel.id} />
          </Stack>
        </Card>
      </Stack>
    </>
  );
}
